'use client';

interface PauseMenuProps {
  onResume: () => void;
  onBackToMenu: () => void;
  soundEnabled: boolean;
  onToggleSound: () => void;
  difficulty: 'easy' | 'normal' | 'hard';
  onDifficultyChange: (d: 'easy' | 'normal' | 'hard') => void;
}

const DIFFICULTIES: { value: 'easy' | 'normal' | 'hard'; label: string; color: string }[] = [
  { value: 'easy',   label: 'LET',    color: '#66bb6a' },
  { value: 'normal', label: 'NORMAL', color: '#00e5ff' },
  { value: 'hard',   label: 'HÅRD',   color: '#ef5350' },
];

export default function PauseMenu({
  onResume,
  onBackToMenu,
  soundEnabled,
  onToggleSound,
  difficulty,
  onDifficultyChange,
}: PauseMenuProps) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(2, 4, 10, 0.82)', backdropFilter: 'blur(6px)' }}
    >
      {/* Panel */}
      <div
        className="relative flex flex-col items-center gap-6 px-10 py-10 rounded-2xl"
        style={{
          background: 'linear-gradient(160deg, rgba(0,20,40,0.95) 0%, rgba(0,10,25,0.98) 100%)',
          border: '1px solid rgba(0, 229, 255, 0.25)',
          boxShadow: '0 0 60px rgba(0, 229, 255, 0.08), 0 0 120px rgba(0, 229, 255, 0.04)',
          minWidth: '320px',
        }}
      >
        {/* Corner accents */}
        <div className="absolute top-3 left-3 w-4 h-4 border-t border-l" style={{ borderColor: 'rgba(0,229,255,0.5)' }} />
        <div className="absolute top-3 right-3 w-4 h-4 border-t border-r" style={{ borderColor: 'rgba(0,229,255,0.5)' }} />
        <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l" style={{ borderColor: 'rgba(0,229,255,0.5)' }} />
        <div className="absolute bottom-3 right-3 w-4 h-4 border-b border-r" style={{ borderColor: 'rgba(0,229,255,0.5)' }} />

        {/* Title */}
        <div className="flex flex-col items-center gap-1">
          <span
            className="font-mono-game text-[10px] tracking-[0.35em] uppercase"
            style={{ color: 'rgba(0, 229, 255, 0.5)' }}
          >
            SPIL
          </span>
          <h2
            className="font-mono-game text-3xl font-800 tracking-widest uppercase"
            style={{
              color: '#00e5ff',
              textShadow: '0 0 20px rgba(0, 229, 255, 0.6)',
            }}
          >
            PAUSE
          </h2>
          <div className="w-16 h-px mt-1" style={{ background: 'linear-gradient(to right, transparent, rgba(0,229,255,0.5), transparent)' }} />
        </div>

        {/* Resume button */}
        <button
          onClick={onResume}
          className="w-full py-3 rounded-lg font-mono-game text-sm font-700 tracking-[0.2em] uppercase transition-all duration-150 active:scale-95"
          style={{
            background: 'linear-gradient(135deg, rgba(0,229,255,0.18) 0%, rgba(0,180,200,0.12) 100%)',
            border: '1px solid rgba(0, 229, 255, 0.5)',
            color: '#00e5ff',
            textShadow: '0 0 10px rgba(0,229,255,0.6)',
            boxShadow: '0 0 16px rgba(0,229,255,0.1)',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, rgba(0,229,255,0.28) 0%, rgba(0,180,200,0.22) 100%)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 24px rgba(0,229,255,0.25)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, rgba(0,229,255,0.18) 0%, rgba(0,180,200,0.12) 100%)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 16px rgba(0,229,255,0.1)';
          }}
        >
          ▶ FORTSÆT
        </button>

        {/* Sound toggle */}
        <div className="w-full flex items-center justify-between">
          <span
            className="font-mono-game text-[11px] tracking-[0.2em] uppercase"
            style={{ color: 'rgba(0,229,255,0.7)' }}
          >
            LYD
          </span>
          <button
            onClick={onToggleSound}
            className="relative flex items-center gap-2 px-4 py-2 rounded-lg font-mono-game text-[11px] tracking-[0.15em] uppercase transition-all duration-150 active:scale-95"
            style={{
              background: soundEnabled
                ? 'rgba(0,229,255,0.12)'
                : 'rgba(255,255,255,0.04)',
              border: `1px solid ${soundEnabled ? 'rgba(0,229,255,0.45)' : 'rgba(255,255,255,0.15)'}`,
              color: soundEnabled ? '#00e5ff' : 'rgba(255,255,255,0.35)',
            }}
          >
            <span style={{ fontSize: '14px' }}>{soundEnabled ? '🔊' : '🔇'}</span>
            <span>{soundEnabled ? 'TIL' : 'FRA'}</span>
          </button>
        </div>

        {/* Divider */}
        <div className="w-full h-px" style={{ background: 'rgba(0,229,255,0.1)' }} />

        {/* Difficulty selector */}
        <div className="w-full flex flex-col gap-2">
          <span
            className="font-mono-game text-[10px] tracking-[0.25em] uppercase"
            style={{ color: 'rgba(0,229,255,0.5)' }}
          >
            SVÆRHEDSGRAD
          </span>
          <div className="flex gap-2">
            {DIFFICULTIES.map((d) => {
              const isActive = difficulty === d.value;
              return (
                <button
                  key={d.value}
                  onClick={() => onDifficultyChange(d.value)}
                  className="flex-1 py-2 rounded-lg font-mono-game text-[10px] tracking-[0.15em] uppercase transition-all duration-150 active:scale-95"
                  style={{
                    background: isActive ? `rgba(${hexToRgb(d.color)}, 0.18)` : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${isActive ? d.color : 'rgba(255,255,255,0.12)'}`,
                    color: isActive ? d.color : 'rgba(255,255,255,0.35)',
                    textShadow: isActive ? `0 0 8px ${d.color}` : 'none',
                    boxShadow: isActive ? `0 0 12px rgba(${hexToRgb(d.color)}, 0.2)` : 'none',
                  }}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-px" style={{ background: 'rgba(0,229,255,0.1)' }} />

        {/* Back to menu */}
        <button
          onClick={onBackToMenu}
          className="w-full py-2.5 rounded-lg font-mono-game text-[11px] tracking-[0.2em] uppercase transition-all duration-150 active:scale-95"
          style={{
            background: 'rgba(255,23,68,0.08)',
            border: '1px solid rgba(255,23,68,0.3)',
            color: 'rgba(255,100,100,0.8)',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,23,68,0.16)';
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,23,68,0.55)';
            (e.currentTarget as HTMLButtonElement).style.color = '#ff6464';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,23,68,0.08)';
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,23,68,0.3)';
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,100,100,0.8)';
          }}
        >
          ← TILBAGE TIL MENU
        </button>

        {/* ESC hint */}
        <span
          className="font-mono-game text-[9px] tracking-widest"
          style={{ color: 'rgba(0,229,255,0.25)' }}
        >
          ESC — FORTSÆT
        </span>
      </div>
    </div>
  );
}

function hexToRgb(hex: string): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `${r}, ${g}, ${b}`;
}
